/**
 * Die Stornobedingungen, wie der Kunde sie sehen soll.
 *
 * Der Vermieter pflegt seine Erstattungsstaffel als Zonen in Tagen vor dem
 * Miettag: { startDays, endDays, refundPercent }. Für ihn ist das die richtige
 * Form — er denkt in Vorlaufzeiten. Für den Kunden ist sie es nicht: Der hat
 * einen Termin im Kopf und will wissen, bis wann er noch aussteigen kann.
 *
 * Deshalb rechnet dieses Modul die Zonen in Kalenderdaten um, sobald der Kunde
 * seinen Miettag gewählt hat. Aus "31–42 Tage vorher: 80 %" wird
 * "12.–23. November: 80 %", und das lässt sich mit einem Blick in den eigenen
 * Kalender beantworten.
 */

/** Tag ohne Uhrzeit — sonst verschiebt die Tageszeit die Differenz. */
function aufTag(wert) {
  const d =
    wert instanceof Date ? new Date(wert) : new Date(`${wert}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function minusTage(datum, tage) {
  const d = new Date(datum);
  d.setDate(d.getDate() - tage);
  return d;
}

/**
 * Was die Prozentzahl in Euro bedeutet — kaufmännisch auf Cent gerundet.
 *
 * Ohne Preis null, nicht 0: Der Unterschied zwischen "kein Preis bekannt" und
 * "null Euro zurück" darf nicht verwischen.
 */
export function betragFuer(prozent, gesamtpreis) {
  if (typeof gesamtpreis !== "number" || !Number.isFinite(gesamtpreis)) {
    return null;
  }
  return Math.round(gesamtpreis * prozent) / 100;
}

/** Ganze Tage zwischen zwei Tagen, kalendarisch statt über Millisekunden. */
export function tageDazwischen(von, bis) {
  const a = aufTag(von);
  const b = aufTag(bis);
  if (!a || !b) return null;
  return Math.round((b - a) / 86_400_000);
}

/** Wie viel Prozent gäbe es bei Absage `tageVorher` Tage vor dem Miettag? */
export function erstattungBei(ranges, tageVorher) {
  const treffer = (ranges || []).find(
    (r) => tageVorher >= r.startDays && tageVorher <= r.endDays
  );
  return treffer ? treffer.refundPercent : null;
}

/**
 * Bringt die Staffel in eine Form, die sich anzeigen lässt.
 *
 * Sortiert nach Vorlauf absteigend — also von "früh abgesagt" nach "spät
 * abgesagt". So liest sie sich von oben nach unten wie der eigene Kalender,
 * und der Betrag sinkt dabei zeilenweise.
 *
 * Lücken werden nicht gefüllt. Fünf der achtzehn stornierbaren Angebote haben
 * Staffeln, die nicht bis 999 Tage reichen (z. B. nur "0–7 Tage: 100 %") —
 * was bei früherer Absage gilt, hat der Vermieter schlicht nicht hinterlegt.
 * Etwas zu erfinden hieße, ihm ein Versprechen in den Mund zu legen. Also
 * wird die Lücke gemeldet und im Frontend benannt.
 *
 * @param cancellation  partner.cancellation
 * @param eventDate     gewählter Miettag ("yyyy-MM-dd"), optional
 * @param heute         für die Markierung "das gilt jetzt", optional
 * @param gesamtpreis   Mietpreis in Euro. Steht er fest, bekommt jede Stufe
 *                      ihren Betrag — "50 %" muss dann niemand mehr im Kopf
 *                      auf den Preis anwenden, der zwei Zeilen weiter oben
 *                      steht.
 */
export function stornoStaffel(
  cancellation,
  { eventDate, heute, gesamtpreis } = {}
) {
  const c = cancellation || {};
  if (!c.cancellable) {
    return { stornierbar: false, stufen: [], luecken: [], jetzt: null };
  }

  const roh = (c.refundRanges || []).filter(
    (r) =>
      Number.isFinite(r?.startDays) &&
      Number.isFinite(r?.endDays) &&
      r.endDays >= r.startDays
  );

  // Nach Vorlauf absteigend: die früheste Absage zuerst.
  const sortiert = [...roh].sort((a, b) => b.startDays - a.startDays);

  const tag = eventDate ? aufTag(eventDate) : null;
  const vorlaufHeute =
    tag && heute !== undefined ? tageDazwischen(heute, tag) : null;

  const stufen = sortiert.map((r) => {
    // Die Zone [startDays, endDays] Tage vorher entspricht dem Zeitraum
    // von (Miettag − endDays) bis (Miettag − startDays).
    const abDatum = tag ? minusTage(tag, r.endDays) : null;
    const bisDatum = tag ? minusTage(tag, r.startDays) : null;
    return {
      vonTag: r.startDays,
      bisTag: r.endDays,
      prozent: r.refundPercent,
      betrag: betragFuer(r.refundPercent, gesamtpreis),
      abDatum,
      bisDatum,
      // Die früheste Zone hat nach vorn keine Grenze ("bis zum 24. Oktober"),
      // die späteste läuft bis zum Miettag ("ab dem 10. Dezember").
      offenNachVorn: r.endDays >= 999,
      laeuftBisMiettag: r.startDays === 0,
      giltJetzt:
        vorlaufHeute !== null &&
        vorlaufHeute >= r.startDays &&
        vorlaufHeute <= r.endDays,
      // Vorbei: Die Zone verlangt mehr Vorlauf, als heute noch da ist. Sie
      // taugt dann nicht mehr als Zusage — wer fünf Tage vor dem Termin
      // bucht, dem nützt "100 % bis 22 Tage vorher" nichts mehr.
      abgelaufen: vorlaufHeute !== null && vorlaufHeute < r.startDays,
    };
  });

  return {
    stornierbar: true,
    stufen,
    luecken: findeLuecken(roh),
    jetzt: vorlaufHeute === null ? null : erstattungBei(roh, vorlaufHeute),
    vorlaufHeute,
  };
}

/**
 * Zeiträume, für die nichts hinterlegt ist — von 0 Tagen bis ganz nach oben.
 *
 * Dieselbe Rechnung wie die Freigabeprüfung im Backend, nur dass sie hier
 * nicht meckert, sondern der Anzeige sagt, wo sie schweigen muss.
 */
export function findeLuecken(ranges) {
  const sortiert = [...(ranges || [])].sort(
    (a, b) => a.startDays - b.startDays
  );
  const luecken = [];
  let erwartet = 0;
  for (const r of sortiert) {
    if (r.startDays > erwartet) {
      luecken.push({ vonTag: erwartet, bisTag: r.startDays - 1 });
    }
    erwartet = Math.max(erwartet, r.endDays + 1);
  }
  if (erwartet <= 999) luecken.push({ vonTag: erwartet, bisTag: null });
  return luecken;
}

const KURZ = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "short",
});
const LANG = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "long",
});

/** "12. Nov." — kurz genug für eine Tabellenzeile. */
export function tagKurz(datum) {
  return datum ? KURZ.format(datum) : "";
}

/** "12. November" — für Fließtext. */
export function tagLang(datum) {
  return datum ? LANG.format(datum) : "";
}

/**
 * Die Beschriftung einer Stufe — mit Datum, wenn der Miettag feststeht, sonst
 * mit Vorlauftagen.
 */
export function stufenLabel(stufe) {
  if (stufe.abDatum && stufe.bisDatum) {
    if (stufe.offenNachVorn) return `bis ${tagKurz(stufe.bisDatum)}`;
    if (stufe.laeuftBisMiettag) return `ab ${tagKurz(stufe.abDatum)}`;
    return `${tagKurz(stufe.abDatum)} – ${tagKurz(stufe.bisDatum)}`;
  }
  // "22 Tage vorher oder früher" statt "ab 22 Tage vorher": Die Zusage
  // darüber sagt "bis 22 Tage vor dem Miettag". Beides meint dasselbe, aber
  // "ab" und "bis" untereinander lesen sich wie Gegenteile. "oder früher"
  // lässt keine Richtung offen.
  if (stufe.offenNachVorn) return `${stufe.vonTag} Tage vorher oder früher`;
  if (stufe.laeuftBisMiettag)
    return `weniger als ${stufe.bisTag + 1} Tage vorher`;
  return `${stufe.vonTag} – ${stufe.bisTag} Tage vorher`;
}

/* ─────────────────────────────────────────────────────────────────────────
   Bewertung: taugt die Regelung als Argument?

   Die Staffel wird nicht abgefragt, um den Kunden aufzuklären — rechtlich
   steht sie in den AGB des Vermieters, die er beim Anfragen ohnehin
   akzeptiert. Sie wird abgefragt, damit wir sie bewerten können: Ist sie gut,
   nimmt sie einem Zweifelnden die Entscheidung ab und gehört sichtbar an den
   Knopf. Ist sie es nicht, gewinnt niemand etwas dadurch, sie ihm vorzuhalten.

   Zwei Regeln, die sich beim Durchdenken als die wichtigen herausgestellt
   haben:

   1. Nur verlustfreie Aussagen werden beworben.
      "Kostenlose Stornierung" ist eine Zusage. "50 % zurück" ist keine — der
      Kunde liest darin nicht die Hälfte, die er bekommt, sondern die Hälfte,
      die er verliert. Verluste wiegen in der Wahrnehmung ungefähr doppelt so
      schwer wie gleich große Gewinne; ein Teilerstattungs-Hinweis direkt am
      Absendeknopf schafft also Zweifel, wo vorher keine waren. Teilstaffeln
      stehen deshalb nur im aufklappbaren Detail, nie im Hinweis.

   2. Nur, was jetzt noch erreichbar ist.
      Wer neun Tage vor dem Termin bucht, dem nützt "kostenlos bis 22 Tage
      vorher" nichts mehr. Eine abgelaufene Zusage ist schlimmer als keine.

   Von 45 sichtbaren Anbietern bleiben damit 35 mit einer positiven Zeile
   (12 volle Erstattung, 33 kostenlose Umbuchung, 10 beides). Die restlichen
   10 schweigen — an einer Stelle, an der ohnehin noch nichts verbindlich ist.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Die beste Erstattung, die dem Kunden jetzt noch offensteht.
 *
 * Ohne Miettag (erster Schritt, der Kunde vergleicht noch) zählt der beste
 * Wert der Staffel — abgelaufen ist dort noch nichts.
 */
function besteErreichbare(stufen) {
  const erreichbar = stufen.filter((s) => !s.abgelaufen);
  if (!erreichbar.length) return null;
  return erreichbar.reduce((a, b) => (b.prozent > a.prozent ? b : a));
}

/**
 * Der eine Hinweis, der an den Knopf gehört — oder keiner.
 *
 * Bewusst nur einer. Der Kunde hat an dieser Stelle genau eine Sorge ("was,
 * wenn etwas dazwischenkommt?"), und die braucht eine Antwort, keine Liste.
 * Zwei grüne Häkchen untereinander lesen sich als Werbung und werden
 * überblättert; eine einzelne Zusage wird gelesen. Kostenloses Stornieren
 * beantwortet die Frage vollständig und schlägt deshalb das Umbuchen — was
 * sonst noch gilt, steht im aufklappbaren Detail.
 *
 * @returns {{
 *   hinweis: {art: "storno"|"umbuchung", text: string, frist: string}|null,
 *   umbuchung: string|null,
 *   staffel: object
 * }}
 */
export function bewerteStorno(
  cancellation,
  { eventDate, heute, gesamtpreis } = {}
) {
  const staffel = stornoStaffel(cancellation, {
    eventDate,
    heute,
    gesamtpreis,
  });
  const c = cancellation || {};

  const beste = staffel.stornierbar ? besteErreichbare(staffel.stufen) : null;
  const vollKostenlos = beste && beste.prozent >= 100 ? beste : null;

  // Auch die Umbuchungsfrist kann schon abgelaufen sein: Wer zehn Tage vor
  // dem Termin bucht, dem "bis 14 Tage vorher kostenlos umbuchen" zuzusagen,
  // wäre ein Versprechen, das im Moment des Lesens nicht mehr gilt.
  const umbuchungAbgelaufen =
    staffel.vorlaufHeute !== null &&
    staffel.vorlaufHeute !== undefined &&
    staffel.vorlaufHeute < (c.freeRescheduleDays ?? 0);

  const umbuchung =
    c.freeReschedule && !umbuchungAbgelaufen
      ? umbuchungsfrist(c.freeRescheduleDays ?? 0, eventDate)
      : null;

  // Eine Staffel mit Lücke ist keine Grundlage für ein Versprechen.
  //
  // Sieben der achtzehn stornierbaren Anbieter haben eine, und alle sieben
  // dasselbe Muster: nur die Zone kurz vor dem Termin gefüllt, nichts für
  // frühe Absagen. Sie haben das Formular andersherum verstanden — "0–7 Tage:
  // 100 %" sollte heißen "100 % bis 7 Tage vorher", bedeutet in der Staffel
  // aber "100 %, wenn du in der letzten Woche absagst".
  //
  // Wörtlich genommen ergäbe das die Zusage "Kostenlose Stornierung bis zum
  // Miettag" — kostenlos absagen noch am Miettag selbst. Das bietet niemand
  // an, und wir dürfen es niemandem in den Mund legen. Solange die Staffel
  // nicht lückenlos ist, wird also nichts zugesagt; die hinterlegten Stufen
  // bleiben im aufklappbaren Detail sichtbar.
  //
  // Die Umbuchung ist davon nicht betroffen: Das ist eine einzelne Zahl, kein
  // Zeitraum, und damit nicht misszuverstehen.
  const staffelVollstaendig = staffel.luecken.length === 0;

  const punkte = [];
  if (vollKostenlos && staffelVollstaendig) {
    punkte.push({
      art: "storno",
      text: "Kostenlose Stornierung",
      frist: frist(vollKostenlos),
    });
  }
  if (umbuchung) {
    // Beide stehen nebeneinander, obwohl Stornieren die stärkere Zusage ist.
    // Sie beantworten verschiedene Fragen: absagen oder verschieben. Für eine
    // Feier ist das Verschieben oft das Näherliegende — ein Termin platzt
    // seltener, als er sich verschiebt.
    punkte.push({
      art: "umbuchung",
      text: "Kostenlose Umbuchung",
      frist: umbuchung,
    });
  }

  return { punkte, staffel };
}

/**
 * Frist fürs Umbuchen — als Datum, sobald der Miettag feststeht. Dieselbe
 * Regel wie beim Stornieren: ein Datum nur dann, wenn der Kunde dafür nichts
 * rechnen muss.
 */
function umbuchungsfrist(tage, eventDate) {
  if (tage === 0) return "bis zum Liefertermin";
  const tag = eventDate ? aufTag(eventDate) : null;
  if (!tag) return `bis ${tage} Tage vor dem Miettag`;
  return `bis ${tagLang(minusTage(tag, tage))}`;
}

/**
 * "bis 2. Dezember", wenn der Miettag feststeht — sonst "bis 22 Tage vor dem
 * Miettag".
 *
 * Bewusst "bis" und nicht "ab": Die Zone beginnt zwar bei 22 Tagen Vorlauf,
 * aber "ab 22 Tage vorher" liest sich für viele in die falsche Richtung, als
 * seien die letzten 22 Tage gemeint. "Kostenlose Stornierung bis X Tage vor …"
 * ist die Form, die jeder von Hotelbuchungen kennt und richtig versteht: X ist
 * der letzte Zeitpunkt, zu dem es noch kostenlos geht.
 */
function frist(stufe) {
  if (stufe.laeuftBisMiettag) return "bis zum Miettag";
  if (stufe.bisDatum) return `bis ${tagLang(stufe.bisDatum)}`;
  return `bis ${stufe.vonTag} Tage vor dem Miettag`;
}
